import notDeleted from '../notDeleted'

/**
 * Decide what should happen to a linked indicator when its effect is deleted.
 *
 * An indicator is a `QuestionnaireFields` doc linked to one or more effects via
 * the `EffectId` array. When an effect is deleted we never want to break another
 * effect, so an indicator that is still linked to a different effect is only
 * unlinked from the deleted one — never removed. Only an indicator linked solely
 * to this effect gets soft-deleted, and only when the user opted in.
 *
 * @typedef {object} Indicator
 * @property {string[]} [EffectId] - IDs of the effects this indicator is linked to
 * @property {boolean|string} [Deleted] - soft-delete flag (ADR-008)
 *
 * @param {Indicator | null | undefined} indicator - the linked indicator doc
 * @param {string} effectId - ID of the effect being deleted
 * @param {boolean} deleteLinkedIndicators - whether the user chose to also delete indicators
 * @returns {'soft-delete' | 'unlink' | 'none'}
 *   `soft-delete` — remove the indicator entirely (solely linked + user opted in);
 *   `unlink` — remove only this effect's ID from the indicator's `EffectId` array;
 *   `none` — do nothing (already deleted, or not linked to this effect).
 */
const resolveIndicatorDeleteAction = (indicator, effectId, deleteLinkedIndicators) => {
    const effectIds = indicator?.EffectId

    if (!indicator || !notDeleted(indicator)) return 'none'
    if (!Array.isArray(effectIds) || !effectIds.includes(effectId)) return 'none'

    const solelyLinkedToThisEffect = effectIds.length === 1

    if (deleteLinkedIndicators && solelyLinkedToThisEffect) return 'soft-delete'

    return 'unlink'
}

export default resolveIndicatorDeleteAction
